import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  FormControl,
  Flex,
  Heading,
  Input,
  Stack,
  Text,
  Image,
  Box,
  useColorModeValue,
  FormErrorMessage,
  Center,
  useToast,
} from "@chakra-ui/react";

import { appRequest } from "routes";
import { BASE_PATH } from "utils/env";

type ForgotPasswordFormEmail = {
  email: string;
};

type ForgotPasswordEmailErrors = {
  email?: string;
};

export default function ForgotPasswordForm(): JSX.Element {
  const Navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(false);

  const [values, setValues] =
    useState<ForgotPasswordFormEmail>({
      email: "",
    });

  const [errors, setErrors] =
    useState<ForgotPasswordEmailErrors>({});

  const validateEmail = () => {
    if (!values.email) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Email is required",
      }));
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Invalid email format",
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: undefined,
      }));
    }
  };

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (!values.email.trim()) {
      return;
    }

    try {
      setLoading(true);

      const response: any = await appRequest(
        "auth",
        "forgotPassword",
        { email: values.email }
      );

      if (response) {
        const { token } = response;

        if (!token) {
          toast({
            title: response.message,
            status: "success",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          });

          Navigate("/login");

          return;
        }
      }
    } catch (error: any) {
      toast({
        title: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-left",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;

    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  return (
    <Flex
      minH={"100vh"}
      align={"center"}
      justify={"center"}
      bg={useColorModeValue("gray.50", "gray.800")}
    >
      <Stack
        spacing={2}
        mx={"auto"}
        w={"full"}
        maxW={"lg"}
        py={12}
        px={6}
      >
        <Stack align={"center"}>
          <Image
            src={BASE_PATH + "/img/sequelstring.webp"}
            width={320}
          />
        </Stack>

        <form onSubmit={handleSubmit}>
          <Box
            rounded={"lg"}
            bg={useColorModeValue("white", "gray.700")}
            boxShadow={"lg"}
            p={8}
          >
            <Heading
              color="teal.300"
              mb="11px"
              lineHeight={1.1}
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="700"
            >
              Forgot your password?
            </Heading>

            <Text
              fontSize={{ base: "sm", sm: "md" }}
              color={useColorModeValue(
                "gray.400",
                "gray.800"
              )}
              fontWeight="700"
              mb={6}
            >
              You'll get an email with a reset link
            </Text>

            <FormControl
              id="email"
              isInvalid={!!errors.email}
            >
              <Input
                type="email"
                placeholder="Your email address"
                name="email"
                value={values.email}
                onChange={handleChange}
                onBlur={validateEmail}
                borderRadius="15px"
                _hover={{
                  border: "2px solid",
                  borderColor: "blue.1000",
                }}
                _focus={{
                  border: "2px solid",
                  borderColor: "blue.1000",
                }}
              />

              <FormErrorMessage>
                {errors.email}
              </FormErrorMessage>
            </FormControl>

            <Stack spacing={6} mt={8}>
              <Button
                bg="teal.300"
                color="white"
                rounded={"lg"}
                h={"45px"}
                fontWeight="700"
                type="submit"
                _hover={{
                  bg: "teal.400",
                }}
                isLoading={loading}
              >
                Request Reset
              </Button>

              <Center>
                <Text
                  color="gray.700"
                  fontSize={{ base: "sm", sm: "md" }}
                >
                  <Text
                    as="span"
                    fontWeight="700"
                    color="teal.300"
                    cursor="pointer"
                    mr={2}
                    _hover={{
                      borderBottom: "2px solid",
                    }}
                    onClick={() => Navigate("/login")}
                  >
                    Click here
                  </Text>
                  to login!
                </Text>
              </Center>
            </Stack>
          </Box>
        </form>
      </Stack>
    </Flex>
  );
}